import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { analysesApi } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('acquiflow_token'));
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // First check if setup is required
        const statusRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/check`);
        const statusData = await statusRes.json();
        setSetupRequired(statusData.setup_required);

        // If we have a token, verify it
        if (token && !statusData.setup_required) {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const userData = await res.json();
            setUser(userData);
          } else {
            // Token invalid
            localStorage.removeItem('acquiflow_token');
            setToken(null);
          }
        }
      } catch (e) {
        console.error('Auth check failed:', e);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Login failed');
    }
    
    const data = await res.json();
    localStorage.setItem('acquiflow_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const setup = useCallback(async (email, password, name) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, role: 'admin' }),
    });
    
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Setup failed');
    }
    
    const data = await res.json();
    localStorage.setItem('acquiflow_token', data.token);
    setToken(data.token);
    setUser(data.user);
    setSetupRequired(false);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('acquiflow_token');
    setToken(null);
    setUser(null);
  }, []);

  const registerUser = useCallback(async (email, password, name, role) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email, password, name, role }),
    });
    
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Registration failed');
    }
    
    return await res.json();
  }, [token]);

  const listUsers = useCallback(async () => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch users');
    return await res.json();
  }, [token]);

  const updateUser = useCallback(async (userId, data) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Update failed');
    }
    return await res.json();
  }, [token]);

  const deleteUser = useCallback(async (userId) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Delete failed');
    }
    return true;
  }, [token]);

  const value = {
    user,
    token,
    loading,
    setupRequired,
    isAdmin: user?.role === 'admin',
    isClient: user?.role === 'client',
    isAuthenticated: !!user,
    login,
    logout,
    setup,
    registerUser,
    listUsers,
    updateUser,
    deleteUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
